import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { Loader2, Save } from "lucide-react";
import Button from "@/shared/components/button";
import Field from "@/shared/components/field";
import { useUserPlan } from "../hooks/useUserPlan";
import { usePlans } from "../hooks/usePlans";
import type AssignPlanRequest from "../models/AssignPlanRequest";
import type { PlanStatus } from "../valueObjects/PlanStatus";
import GlassBackground from "@/shared/components/glassBackground";
import BackButton from "@/shared/components/backButton";

const PLAN_STATUS_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "No Pagado" },
  { value: 1, label: "Cancelado" },
  { value: 2, label: "Pagado" },
];

export default function AssignPlanPage() {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getUserPlan, assignPlan, updateStatus, loading, error } =
    useUserPlan();
  const { getAllPlans, loading: plansLoading } = usePlans();

  const [planId, setPlanId] = useState("");
  const [status, setStatus] = useState<PlanStatus>(0);
  const [planOptions, setPlanOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [hasExistingPlan, setHasExistingPlan] = useState(false);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    let ignore = false;
    const fetchPlans = async () => {
      const page = await getAllPlans({ pageNumber: 1, pageSize: 100 });
      if (!ignore && page) {
        setPlanOptions(
          page.items.map((p) => ({ value: p.id, label: p.title ?? "" })),
        );
      }
    };
    fetchPlans();
    return () => { ignore = true; };
  }, [getAllPlans]);

  useEffect(() => {
    if (!userId) return;
    let ignore = false;
    const fetchUserPlan = async () => {
      const userPlan = await getUserPlan(userId);
      if (!ignore) {
        if (userPlan) {
          setPlanId(userPlan.planId);
          setStatus(userPlan.status);
          setHasExistingPlan(true);
        }
        setInitializing(false);
      }
    };
    fetchUserPlan();
    return () => { ignore = true; };
  }, [userId, getUserPlan]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    if (hasExistingPlan) {
      await updateStatus(userId, status);
    } else {
      const request: AssignPlanRequest = { userId, planId, status };
      await assignPlan(request);
    }
    navigate(`/admin/accounts/${userId}`);
  };

  return (
    <GlassBackground>
      <div className="relative flex-1 overflow-auto p-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <BackButton text={"Volver a la cuenta"} link={`/admin/accounts/${userId}`}>
            <h2 className="text-[40px] font-bold tracking-tight text-foreground">
              {hasExistingPlan ? "Actualizar plan" : "Asignar plan"}
            </h2>
            <p className="text-foreground/50 text-lg font-medium mt-1">
              {hasExistingPlan
                ? "Modifica el estado del plan asignado al usuario."
                : "Asigna un plan de suscripción al usuario."}
            </p>
          </BackButton>

          {/* Form Card */}
          <div className="bg-white/60 border border-black/5 backdrop-blur-md rounded-4xl p-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)]">
            {(initializing || plansLoading) && (
              <div className="flex justify-center py-10">
                <Loader2 className="size-8 animate-spin text-accent" />
              </div>
            )}

            {error && (
              <p className="text-red-500 text-sm mb-6">
                Error: {error.message}
              </p>
            )}

            {!initializing && !plansLoading && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <Field
                  label="Plan"
                  name="planId"
                  type="select"
                  value={planId}
                  onChange={(e) => setPlanId(e.target.value)}
                  placeholder="Selecciona un plan..."
                  options={planOptions}
                  disabled={hasExistingPlan}
                  required
                />

                <Field
                  label="Estado"
                  name="status"
                  type="select"
                  value={status}
                  onChange={(e) =>
                    setStatus(Number(e.target.value) as PlanStatus)
                  }
                  options={PLAN_STATUS_OPTIONS}
                />

                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/admin/accounts/${userId}`)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    submit
                    disabled={loading}
                    className="h-11 px-6 rounded-xl bg-accent hover:bg-accent/90 text-white border-none shadow-[0_0_20px_rgba(12,123,254,0.3)]"
                  >
                    {loading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Save className="size-4" />
                    )}
                    {hasExistingPlan ? "Actualizar estado" : "Asignar plan"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </GlassBackground>
  );
}
